<template>
  <div class="keyword-selector" :class="{ 'keyword-selector--panel': alignActionsToPanel }">
    <div class="keyword-content">
      <p class="description" v-if="description">{{ description }}</p>
      <m-chip-set
        v-if="selectedTags && selectedTags.length"
        class="keyword-chips"
      >
        <m-chip
          v-for="subject in selectedTags"
          :key="subject"
          shouldRemoveOnTrailingIconClick="false"
        >{{ subject }}</m-chip>
      </m-chip-set>
    </div>

    <div class="keyword-actions" :class="{ 'keyword-actions--panel': alignActionsToPanel }">
      <button
        v-if="canEdit"
        type="button"
        :title="$t('properties.keywords-action-tooltip')"
        :aria-label="$t('properties.keywords-action-tooltip')"
        :disabled="isUpdating"
        @click="showPicker"
      >
        <m-icon icon="label" />
        {{ isUpdating ? "Updating..." : (selectedTags.length ? "Change..." : "Add...") }}
      </button>
    </div>

    <Popup
      v-if="isPickerVisible"
      :defaultButtons="false"
      @cancelled="cancelPicker"
    >
      <template v-slot:header>{{ $t("keywords-heading") }}</template>
      <template v-slot:body>
        <div class="keyword-popup">
          <input
            ref="keywordSearch"
            v-model="keywordQuery"
            type="search"
            class="keyword-search"
            :placeholder="$t('keywords-search-placeholder')"
            @keydown.enter.prevent="selectFirstFilteredKeyword"
            @keydown.backspace="removeLastKeywordWhenSearchEmpty"
          />

          <div class="keyword-group">
            <h6>{{ $t("keywords-selected-heading") }}</h6>
            <div v-if="draftTags.length" class="selected-keywords">
              <button
                v-for="tag in draftTags"
                :key="tag"
                type="button"
                class="keyword-chip"
                :disabled="!canRemoveKeyword(tag)"
                @click="removeDraftTag(tag)"
              >
                <span>{{ tag }}</span>
                <m-icon icon="cancel" />
              </button>
            </div>
            <p v-else class="empty">{{ $t("keywords-selected-empty") }}</p>
          </div>

          <div class="keyword-group">
            <h6>{{ $t("keywords-available-heading") }}</h6>
            <div v-if="filteredAvailableKeywords.length" class="keyword-results">
              <button
                v-for="subject in filteredAvailableKeywords"
                :key="subject.id || subject.label"
                type="button"
                @click="addDraftTag(subject.label)"
              >
                {{ subject.label }}
              </button>
            </div>
            <p v-else class="empty">{{ $t("keywords-available-empty") }}</p>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <div class="popup-actions">
          <button type="button" @click.stop="cancelPicker">
            {{ $t("cancel") }}
          </button>
          <button type="button" class="primary" :disabled="isUpdating || !hasDraftTagsChanged" @click="confirmPicker">
            {{ isUpdating ? "Updating..." : $t("ok") }}
          </button>
        </div>
      </template>
    </Popup>
  </div>
</template>

<script>
import Popup from "./Popup.vue";

export default {
  components: { Popup },
  props: {
    id: {
      type: String,
      default: "add-tag",
    },
    allSubjects: {
      type: Array,
      required: true,
    },
    selectedTags: {
      type: Array,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
    alignActionsToPanel: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["tags-changed"],
  data() {
    return {
      selectedKeyword: "",
      draftTags: [],
      keywordQuery: "",
      isPickerVisible: false,
    };
  },
  computed: {
    canEdit() {
      return !this.disabled || this.canDelete;
    },
    hasDraftTagsChanged() {
      return !this.arraysEqual(this.normalizedTags(this.draftTags), this.normalizedTags(this.selectedTags));
    },
    filteredAvailableKeywords() {
      const query = this.keywordQuery.trim().toLowerCase();
      return (this.allSubjects || [])
        .filter((subject) => subject && subject.label && !this.draftTags.includes(subject.label))
        .filter((subject) => !query || subject.label.toLowerCase().includes(query));
    },
  },
  methods: {
    normalizedTags(tags = []) {
      return (tags || []).map((tag) => String(tag).trim()).filter(Boolean).sort();
    },
    arraysEqual(a, b) {
      if (a.length !== b.length) {
        return false;
      }
      return a.every((value, index) => value === b[index]);
    },
    showPicker() {
      this.draftTags = [...this.selectedTags];
      this.keywordQuery = "";
      this.isPickerVisible = true;
      this.$nextTick(() => {
        this.$refs.keywordSearch?.focus();
      });
    },
    cancelPicker() {
      this.draftTags = [];
      this.keywordQuery = "";
      this.isPickerVisible = false;
    },
    confirmPicker() {
      if (this.isUpdating || !this.hasDraftTagsChanged) {
        return;
      }
      this.$emit("tags-changed", [...this.draftTags]);
      this.cancelPicker();
    },
    canRemoveKeyword(tag) {
      return this.canDelete || !this.selectedTags.includes(tag);
    },
    toggleDraftTag(tag) {
      if (this.draftTags.includes(tag)) {
        this.removeDraftTag(tag);
        return;
      }
      this.addDraftTag(tag);
    },
    addDraftTag(tag) {
      if (!tag || this.disabled || this.draftTags.includes(tag)) {
        return;
      }
      this.draftTags = [...this.draftTags, tag];
      this.keywordQuery = "";
      this.$nextTick(() => {
        this.$refs.keywordSearch?.focus();
      });
    },
    removeDraftTag(tag) {
      if (!this.canRemoveKeyword(tag)) {
        return;
      }
      this.draftTags = this.draftTags.filter((draftTag) => draftTag !== tag);
    },
    selectFirstFilteredKeyword() {
      const first = this.filteredAvailableKeywords[0];
      if (first) {
        this.addDraftTag(first.label);
      }
    },
    removeLastKeywordWhenSearchEmpty(event) {
      if (this.keywordQuery || !this.draftTags.length) {
        return;
      }
      const last = this.draftTags[this.draftTags.length - 1];
      if (this.canRemoveKeyword(last)) {
        event.preventDefault();
        this.removeDraftTag(last);
      }
    },
    tagAdded(event) {
      const value = event && event.value ? event.value : this.selectedKeyword;
      if (!value || this.selectedTags.includes(value)) {
        this.selectedKeyword = "";
        return;
      }
      this.$emit("tags-changed", [...this.selectedTags, value]);
      this.$nextTick(() => {
        this.selectedKeyword = "";
      });
    },
    tagRemoved(tag) {
      this.$emit("tags-changed", this.selectedTags.filter((s) => s !== tag));
    },
  },
};
</script>

<style lang="scss" scoped>
.keyword-selector {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  gap: 1rem;
  align-items: start;
}

.keyword-selector--panel {
  display: grid;
}

.keyword-actions {
  justify-self: end;
}

.keyword-actions--panel {
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
}

.keyword-actions--panel > button {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  width: 10.5rem;
}

@media (max-width: 900px) {
  .keyword-selector {
    grid-template-columns: 1fr;
  }

  .keyword-actions {
    position: static;
    justify-self: start;
  }
}

.description {
  margin-bottom: 1.25rem;
}

.keyword-popup {
  display: grid;
  gap: 1rem;
  min-width: min(34rem, 80vw);
}

.keyword-search {
  border: 1px solid #C8D2DC;
  border-radius: 4px;
  font: inherit;
  padding: 0.65rem 0.75rem;
  width: 100%;
}

.keyword-group {
  display: grid;
  gap: 0.5rem;

  h6 {
    font-size: 0.875rem;
    font-weight: bold;
    margin: 0;
  }
}

.selected-keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.keyword-chip {
  align-items: center;
  background: #eef5fd;
  border: 1px solid #c8dff5;
  border-radius: 999px;
  color: #075a9d;
  display: inline-flex;
  gap: 0.35rem;
  line-height: 1.2;
  padding: 0.35rem 0.6rem;

  > i {
    font-size: 1rem;
    padding-right: 0;
  }
}

.keyword-results {
  border: 1px solid #e0e0e0;
  max-height: 14rem;
  overflow-y: auto;

  button {
    background: white;
    border: 0;
    border-bottom: 1px solid #eeeeee;
    color: #111;
    display: block;
    font-weight: normal;
    padding: 0.55rem 0.75rem;
    text-align: left;
    width: 100%;

    &:hover {
      background: #eef5fd;
    }
  }
}

.empty {
  color: #767676;
  margin: 0;
}

.popup-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

button {
  background: transparent;
  border: 1px solid #1275cc;
  color: #1275cc;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-weight: bold;
  cursor: pointer;
  line-height: 1.6rem;
  text-align: center;

  &.primary {
    background: #1275cc;
    border-color: transparent;
    color: white;
    min-width: 5rem;
    width: auto;
  }

  &:disabled {
    cursor: default;
    opacity: 0.2;
  }

  > i {
    vertical-align: -0.35rem;
    padding-right: 0.25rem;
  }
}
</style>
